import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { SubscriptionManager } from '@zelis/platform-ui-components';
import { Breakpoints } from '@classes/breakpoints.class';
import { TelehealthService } from '@services/telehealth/telehealth.service';
import { TrackByUtilities } from '@utilities/trackByFn.utilities';
import { TelehealthVendor } from '@interfaces/telehealth-vendor.model';
import { GoogleTagManagerService } from '@services/analytics/google-tag-manager.service';
import { TranslateService } from '@ngx-translate/core';
import { ExpansionPanelData } from '@zelis/dls/expansion-panel';

@Component({
  selector: 'app-telehealth',
  templateUrl: './telehealth.component.html',
  styleUrls: ['./telehealth.component.scss'],
})
export class TelehealthComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public searchVendors: string[];
  @Input() public expand: boolean;
  @Input() public displayAll: boolean;
  @Input() public showMobileOnly: boolean;

  public vendors: TelehealthVendor[];
  public trackByCd: any = TrackByUtilities.getTrackByFn('teleHealthProdType');
  public panelData: ExpansionPanelData[] = [];
  private subscriptions = new SubscriptionManager();

  constructor(
    public breakpoints: Breakpoints,
    private telehealthService: TelehealthService,
    private googleTagManagerService: GoogleTagManagerService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.getVendors();
  }

  ngOnChanges() {
    this.getVendors();
  }

  ngOnDestroy() {
    this.subscriptions.destroy();
  }

  public logPanelToggle(panelState: string, vendorName: string): void {
    this.googleTagManagerService.pushToDataLayer({
      event: 'telehealth_card_toggle',
      telehealth_card_state: panelState,
      vendor_name: vendorName,
    });
  }

  public logLinkClick(vendorName: string): void {
    this.googleTagManagerService.pushToDataLayer({
      event: 'telehealth_vendor_link',
      vendor_name: vendorName,
    });
  }
  public logAnalytics = (event) => {
    switch (event.action) {
      case 'panel_toggle_open': {
        this.logPanelToggle('open', event.name);
        break;
      }
      case 'panel_toggle_close': {
        this.logPanelToggle('closed', event.name);
        break;
      }
      default: {
        this.logLinkClick(event.name);
        break;
      }
    }
  };
  private getVendors(): void {
    this.subscriptions.add(
      this.telehealthService.vendors.subscribe(
        (vendors: TelehealthVendor[]) => {
          this.vendors = this.displayAll
            ? vendors
            : this.filterBySearch(vendors);

          this.mapPanelData();
        }
      )
    );
  }
  private mapPanelData() {
    this.panelData = this.vendors.map((v) => {
      const vendorName = this.translateService.instant(v.name);

      const panel: ExpansionPanelData = {
        title: vendorName,
        description: v.description
          ? this.translateService.instant(v.description)
          : '',
        content: v.secondary_text
          ? this.translateService.instant(v.secondary_text)
          : '',
        avatarSrc: v.logo ? this.translateService.instant(v.logo) : null,
        buttonContent: v.link_format
          ? this.translateService.instant(v.link_format, {
              vendor_name: vendorName,
            })
          : null,
        href: v.link,
        target: '_blank',
        external_link_indicator: v.external_link_indicator,
      };

      if (v.wait_time || v.phone) {
        panel.telehealth = {
          waitTime: v.wait_time
            ? this.translateService.instant(v.wait_time)
            : null,
          phone: v.phone ? this.translateService.instant(v.phone) : null,
        };
      }

      return panel;
    });
  }

  private filterBySearch(vendors: TelehealthVendor[]): TelehealthVendor[] {
    return this.telehealthService.getFilteredVendors(
      vendors,
      this.searchVendors
    );
  }
}
